import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Printer, X } from "lucide-react";
import { inr, dmy, dmyTime, titleize } from "@/lib/format";
import { ghostBtnCls, primaryBtnCls } from "@/components/Kit";
import { api } from "@/lib/api";

/**
 * Display name of the staff member who recorded an entry (payment, note, …).
 *
 * `by_name` is written server-side from the authenticated actor at record time,
 * so it is the trusted identity. `by` holds the actor's email and is deliberately
 * NOT displayed: an email address is not the staff member's name and looks wrong
 * on a receipt. Legacy entries recorded before `by_name` existed fall back to a
 * neutral label rather than leaking an address or rendering a blank.
 */
export function staffName(entry, fallback = "Staff") {
  const name = (entry?.by_name || entry?.recorded_by_name || "").trim();
  if (name) return name;
  return fallback;
}


/**
 * Single Preview → Print → Cancel workflow for every printable clinical/financial document.
 * "Save as PDF" is the browser print dialog's PDF destination, so one code path serves both.
 */
function PreviewDocumentViewport({ children, paperW, template, testid }) {
  const viewportRef = useRef(null);
  const paperRef = useRef(null);
  const [page, setPage] = useState({
    hasPrescriptionPad: false,
    height: 0,
    scale: 1,
    width: paperW,
  });

  const syncPageBounds = useCallback(() => {
    const viewport = viewportRef.current;
    const paper = paperRef.current;
    const pad = paper?.querySelector("[data-rx-pad]");
    if (!viewport || !paper || !pad) {
      setPage((current) => current.hasPrescriptionPad
        ? { hasPrescriptionPad: false, height: 0, scale: 1, width: paperW }
        : current);
      return;
    }

    const availableWidth = Math.max(viewport.clientWidth - 32, 1);
    const availableHeight = Math.max(viewport.clientHeight - 32, 1);
    const width = pad.offsetWidth;
    const height = pad.offsetHeight;
    const scale = Math.min(1, availableWidth / width, availableHeight / height);
    const next = {
      hasPrescriptionPad: true,
      height,
      scale,
      width,
    };

    setPage((current) => (
      current.hasPrescriptionPad === next.hasPrescriptionPad
      && current.width === next.width
      && current.height === next.height
      && Math.abs(current.scale - next.scale) < 0.001
        ? current
        : next
    ));
  }, [paperW]);

  useLayoutEffect(() => {
    syncPageBounds();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(syncPageBounds);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [syncPageBounds]);

  const frameStyle = page.hasPrescriptionPad
    ? {
      height: `${page.height * page.scale}px`,
      width: `${page.width * page.scale}px`,
    }
    : { width: "100%" };
  const scaleStyle = page.hasPrescriptionPad
    ? {
      transform: `scale(${page.scale})`,
      transformOrigin: "top left",
      width: `${page.width}px`,
    }
    : undefined;

  return (
    <div
      ref={viewportRef}
      className="document-preview-viewport min-h-0 flex-1 overflow-auto bg-slate-200/80"
      data-testid={`${testid}-viewport`}
    >
      <div
        className="document-preview-stage flex min-h-full min-w-full items-start justify-center p-4"
        data-testid={`${testid}-stage`}
      >
        <div className="document-preview-page-frame shrink-0" style={frameStyle}>
          <div className="document-preview-scale" style={scaleStyle}>
            <div
              ref={paperRef}
              id="print-doc-root"
              className={`print-area bg-white text-[12.5px] leading-relaxed text-slate-900 ${
                page.hasPrescriptionPad ? "p-0" : "p-8 sm:p-12"
              }`}
              style={{ width: paperW, ...documentStyle(template) }}
              data-testid={`${testid}-paper`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PrintPreview({ open, onClose, title, subtitle, children, testid = "print-preview",
  paperSize = "A4", template = null, prescriptionPreview = false }) {
  if (!open) return null;
  const paperW = paperSize === "A5" ? 559 : 794;
  const headerClass = [
    "no-print flex flex-wrap items-center justify-between gap-3 border-b border-slate-700",
    "bg-[#0F172A] px-5 py-4",
    prescriptionPreview ? "shrink-0" : "sticky top-0 z-10",
  ].join(" ");
  const printButtonClass = primaryBtnCls
    .replace("bg-[#0F172A]", "bg-[#EA580C]")
    .replace("hover:bg-[#1E293B]", "hover:bg-[#C2410C]");
  const cancelButtonClass = [
    ghostBtnCls,
    "!border-slate-600 !bg-transparent !text-slate-200 hover:!border-white",
    "hover:!text-white",
  ].join(" ");
  const header = (
    <div className={headerClass}>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#EA580C]">Document preview</p>
        <p className="font-ui text-sm font-semibold text-white">{title}</p>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => window.print()}
          data-testid={`${testid}-print-btn`}
          className={printButtonClass}
        >
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testid}-cancel-btn`}
          className={cancelButtonClass}
        >
          <X className="h-4 w-4" /> Cancel
        </button>
      </div>
    </div>
  );

  return createPortal(
    <div
      className={`document-print-overlay fixed inset-0 z-[60] bg-slate-900/60 ${
        prescriptionPreview ? "flex overflow-hidden p-0 sm:p-6" : "overflow-y-auto p-0 sm:p-6"
      }`}
      data-testid={testid}
    >
      <style>{`@media print { @page { size: ${paperSize} portrait; margin: 0; } }`}</style>
      {prescriptionPreview ? (
        <div
          className={[
            "document-preview-shell mx-auto flex h-full w-full flex-col overflow-hidden",
            "bg-white shadow-2xl",
          ].join(" ")}
          style={{ maxWidth: paperW + 8 }}
        >
          {header}
          <PreviewDocumentViewport paperW={paperW} template={template} testid={testid}>
            {children}
          </PreviewDocumentViewport>
        </div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: paperW + 8 }}>
          {header}
          <div
            id="print-doc-root"
            className="print-area bg-white p-8 sm:p-12 text-[12.5px] leading-relaxed text-slate-900"
            style={documentStyle(template)}
            data-testid={`${testid}-paper`}
          >
            {children}
          </div>
        </div>
      )}
    </div>, document.body
  );
}

// Physical paper dimensions (mm) — shared by the pad renderer and print @page size.
export const PAPER_MM = { A4: { w: 210, h: 297 }, A5: { w: 148, h: 210 } };
const ZONE_DEFAULTS = {
  A4: { patient: 50, clinical: 95, treatment: 125, investigations: 205, signature: 255 },
  A5: { patient: 34, clinical: 62, treatment: 82, investigations: 150, signature: 182 },
};
export function defaultLayout(paper = "A4") {
  const z = ZONE_DEFAULTS[paper] || ZONE_DEFAULTS.A4;
  return { left_mm: 12, right_mm: 12,
    zones: Object.fromEntries(Object.entries(z).map(([k, top]) => [k, { top_mm: top, enabled: true }])) };
}

export const PRESCRIPTION_PAD_FIELDS = [
  { key: "patient_name", label: "Patient Name", x: 20, y: 48, w: 82 },
  { key: "uhid", label: "UHID", x: 110, y: 48, w: 35 },
  { key: "date", label: "Date", x: 150, y: 48, w: 38 },
  { key: "age", label: "Age", x: 20, y: 58, w: 25 },
  { key: "gender", label: "Gender", x: 52, y: 58, w: 28 },
  { key: "patient_phone", label: "Contact Number", x: 88, y: 58, w: 48 },
  { key: "doctor", label: "Doctor", x: 20, y: 68, w: 78 },
  { key: "token_number", label: "Token Number", x: 110, y: 68, w: 30 },
  { key: "bp", label: "BP", x: 150, y: 68, w: 30 },
  { key: "temperature", label: "Temperature", x: 20, y: 78, w: 32 },
  { key: "spo2", label: "SpO2", x: 58, y: 78, w: 27 },
  { key: "pulse", label: "Pulse / PR", x: 92, y: 78, w: 30 },
  { key: "weight", label: "Weight", x: 130, y: 78, w: 30 },
  { key: "c_o", label: "C/o", x: 20, y: 88, w: 170, h: 10 },
];

export function defaultPrescriptionFieldLayout(paper = "A4") {
  const scale = paper === "A5" ? 0.68 : 1;
  return Object.fromEntries(PRESCRIPTION_PAD_FIELDS.map((field) => [
    field.key,
    {
      x_mm: Number((field.x * scale).toFixed(1)),
      y_mm: Number((field.y * scale).toFixed(1)),
      width_mm: Number((field.w * scale).toFixed(1)),
      height_mm: Number(((field.h || 7) * scale).toFixed(1)),
      font_size_pt: 10,
      font_family: "",
      label: field.label,
      label_mode: "label_value",
      align: "left",
      visible: true,
    },
  ]));
}

export function documentStyle(template) {
  const family = {
    clinical: '"Trebuchet MS", Verdana, sans-serif',
    serif: 'Georgia, "Times New Roman", serif',
    modern: '"DM Sans", "Helvetica Neue", sans-serif',
  }[template?.font_family || "clinical"];
  return { fontFamily: family, fontSize: `${template?.font_size_pt || 11}pt` };
}

// Renders the uploaded prescription pad/letterhead as a full-page background with
// dynamic content absolutely positioned by the stored millimetre layout. The SAME
// component drives both the Settings live preview and the printed PDF, so coordinates
// and rendering source are identical.
const vitalFrom = (rx, keys, pattern) => {
  const vitals = rx?.vitals;
  if (vitals && typeof vitals === "object" && !Array.isArray(vitals)) {
    const match = keys.map((key) => vitals[key]).find(Boolean);
    if (match !== undefined) return String(match);
  }
  const raw = typeof vitals === "string" ? vitals : "";
  const found = raw.match(pattern);
  return found?.[1]?.trim() || "—";
};

const padFieldValue = (key, rx, patient) => {
  const age = rx?.patient_age ?? patient?.age;
  const values = {
    patient_name: rx?.patient_name || "—",
    uhid: rx?.patient_uhid || "—",
    patient_phone: rx?.patient_phone || patient?.phone || "—",
    age: age === undefined || age === null || age === "" ? "—" : `${age} yrs`,
    gender: titleize(rx?.patient_gender || patient?.gender || "—"),
    date: rx?.created_at ? dmy(rx.created_at) : "—",
    doctor: rx?.doctor_name ? `Dr. ${rx.doctor_name}` : "—",
    token_number: rx?.token_no || rx?.token_number || "—",
    bp: vitalFrom(
      rx,
      ["bp", "blood_pressure"],
      /(?:bp|blood pressure)\s*[:\-]?\s*([^·,;]+)/i,
    ),
    temperature: vitalFrom(
      rx,
      ["temperature", "temp"],
      /(?:temperature|temp)\s*[:\-]?\s*([^·,;]+)/i,
    ),
    spo2: vitalFrom(
      rx,
      ["spo2", "sp_o2"],
      /(?:spo2|sp\s*o2)\s*[:\-]?\s*([^·,;]+)/i,
    ),
    pulse: vitalFrom(
      rx,
      ["pulse", "pr"],
      /(?:pulse|pr)\s*[:\-]?\s*([^·,;]+)/i,
    ),
    weight: vitalFrom(
      rx,
      ["weight", "weight_kg"],
      /weight\s*[:\-]?\s*([^·,;]+)/i,
    ),
    c_o: rx?.symptoms || rx?.chief_complaint || "—",
  };
  return values[key] || "—";
};

const fieldConfig = (template, key, paper) => ({
  ...defaultPrescriptionFieldLayout(paper)[key],
  ...(template?.field_layout?.[key] || {}),
});

function PositionedPadField({ config, field, value, editable, onMove, paper }) {
  const moveStart = (event) => {
    if (!editable || event.button !== 0) return;
    event.preventDefault();
    const surface = event.currentTarget.closest("[data-rx-pad]");
    if (!surface) return;
    const bounds = surface.getBoundingClientRect();
    const dimensions = PAPER_MM[paper] || PAPER_MM.A4;
    const start = { x: event.clientX, y: event.clientY, left: config.x_mm, top: config.y_mm };
    const move = (next) => {
      const x = Math.max(0, Math.min(dimensions.w - config.width_mm,
        start.left + (next.clientX - start.x) * dimensions.w / bounds.width));
      const y = Math.max(0, Math.min(dimensions.h - config.height_mm,
        start.top + (next.clientY - start.y) * dimensions.h / bounds.height));
      onMove(field.key, { x_mm: Number(x.toFixed(1)), y_mm: Number(y.toFixed(1)) });
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
  };
  const content = config.label_mode === "label_only"
    ? config.label
    : config.label_mode === "value_only"
      ? value
      : `${config.label}: ${value}`;
  return (
    <div
      aria-label={`${field.label} position`}
      className={editable ? "cursor-move outline outline-1 outline-dashed outline-orange-500" : ""}
      data-testid={`rx-pad-field-${field.key}`}
      onPointerDown={moveStart}
      style={{
        position: "absolute",
        top: `${config.y_mm}mm`,
        left: `${config.x_mm}mm`,
        width: `${config.width_mm}mm`,
        height: `${config.height_mm}mm`,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        textAlign: config.align,
        lineHeight: 1.25,
        fontSize: `${config.font_size_pt}pt`,
        fontFamily: config.font_family || undefined,
        userSelect: editable ? "none" : "auto",
      }}
    >
      {content}
    </div>
  );
}

export function RxPad({
  template,
  rx,
  patient,
  business,
  editable = false,
  onFieldLayoutChange,
}) {
  const paper = template?.paper_size || "A4";
  const size = PAPER_MM[paper] || PAPER_MM.A4;
  const L = (template?.layout && template.layout.zones) ? template.layout : defaultLayout(paper);
  const Z = L.zones;
  const lm = L.left_mm ?? 12, rm = L.right_mm ?? 12;
  const mode = template?.prescription_mode || "digital";
  const bg = template?.letterhead_image;
  const hasFieldLayout = Object.keys(template?.field_layout || {}).length > 0;
  const box = (z) => ({ position: "absolute", top: `${Z[z]?.top_mm ?? 0}mm`, left: `${lm}mm`, right: `${rm}mm` });
  const on = (z) => Z[z]?.enabled !== false;
  const S = (key, dflt = true) => tShow(template, key, dflt);
  const ageSex = [patient?.gender && titleize(patient.gender),
    (rx?.patient_age ?? patient?.age) ? `${rx?.patient_age ?? patient?.age} yrs` : null,
    patient?.blood_group].filter(Boolean).join(" · ");
  const meds = rx?.medicines || [];
  const invest = rx?.investigations || [];
  return (
    <div data-rx-pad className="relative bg-white text-slate-900" style={{
      width: `${size.w}mm`, height: `${size.h}mm`, overflow: "hidden",
      backgroundImage: bg ? `url(${bg})` : undefined, backgroundSize: "100% 100%", backgroundRepeat: "no-repeat",
      WebkitPrintColorAdjust: "exact", printColorAdjust: "exact",
      ...documentStyle(template),
    }}>
      {!bg && (
        <div
          className="absolute left-[12mm] right-[12mm] top-[12mm] border-b border-slate-800 pb-2"
        >
          <p className="font-ui text-[15pt] font-bold uppercase">{business?.name || "Hospital"}</p>
          <p className="text-[8.5pt] text-slate-600">
            {[business?.address, business?.phone].filter(Boolean).join(" · ")}
          </p>
          <p className="text-right text-[9pt] font-semibold text-[#EA580C]">Prescription</p>
        </div>
      )}
      {hasFieldLayout && PRESCRIPTION_PAD_FIELDS.map((field) => {
        const config = fieldConfig(template, field.key, paper);
        if (!S(field.key, false) || config.visible === false) return null;
        return (
          <PositionedPadField
            key={field.key}
            config={config}
            editable={editable}
            field={field}
            onMove={onFieldLayoutChange}
            paper={paper}
            value={padFieldValue(field.key, rx, patient)}
          />
        );
      })}
      {!hasFieldLayout && on("patient") && (
        <div style={{ ...box("patient"), fontSize: "1em" }} className="leading-snug" data-testid="rx-zone-patient">
          <div className="flex flex-wrap gap-x-6 gap-y-0.5">
            {S("patient_name") && <span><b>Patient:</b> {rx?.patient_name || "—"}</span>}
            {S("age_sex") && !(S("age", false) || S("gender", false)) && (
              <span><b>Age/Sex:</b> {ageSex || "—"}</span>
            )}
            {S("age", false) && <span><b>Age:</b> {rx?.patient_age ?? patient?.age ?? "—"}</span>}
            {S("gender", false) && <span><b>Gender:</b> {titleize(rx?.patient_gender || patient?.gender || "—")}</span>}
            {S("uhid") && <span><b>UHID:</b> {rx?.patient_uhid || "—"}</span>}
            {S("patient_phone") && <span><b>Phone:</b> {rx?.patient_phone || patient?.phone || "—"}</span>}
            {S("date") && <span><b>Date:</b> {dmy(rx?.created_at)}</span>}
            {S("doctor") && <span><b>Doctor:</b> {rx?.doctor_name ? `Dr. ${rx.doctor_name}` : "—"}</span>}
            {S("doctor_qualification") && <span><b>Qualification:</b> {rx?.doctor_qualification || "—"}</span>}
            {S("doctor_registration") && <span><b>Doctor Reg.:</b> {rx?.doctor_registration || "—"}</span>}
            {S("department") && <span><b>Department:</b> {rx?.department_name || rx?.department || "—"}</span>}
            {S("registration_no") && <span><b>Registration no.:</b> {rx?.visit_id || rx?.admission_id || "—"}</span>}
            {S("prescription_no") && <span><b>Prescription no.:</b> {rx?.prescription_no || "—"}</span>}
          </div>
        </div>
      )}
      {on("clinical") && mode === "digital" && (
        <div style={{ ...box("clinical"), fontSize: "1em" }} data-testid="rx-zone-clinical">
          {S("symptoms") && <p className="whitespace-pre-wrap"><b>Symptoms:</b> {rx?.symptoms || "—"}</p>}
          {S("diagnosis") && <p className="whitespace-pre-wrap"><b>Diagnosis:</b> {rx?.diagnosis || "—"}</p>}
          {S("clinical_notes") && <p className="whitespace-pre-wrap"><b>Clinical notes:</b> {rx?.clinical_notes || "—"}</p>}
          {S("treatment") && <p className="whitespace-pre-wrap"><b>Treatment:</b> {rx?.treatment || "—"}</p>}
        </div>
      )}
      {on("treatment") && mode === "digital" && (
        <div style={{ ...box("treatment"), fontSize: "1em" }} data-testid="rx-zone-treatment">
          <p style={{ fontSize: "0.78em" }} className="font-bold uppercase tracking-wider text-slate-500">Rx — treatment</p>
          <table className="mt-1 w-full border-collapse">
            <thead><tr className="border-b border-slate-300 text-left"><th className="py-0.5 w-5">#</th><th>Medicine</th>{S("strength") && <th>Strength</th>}{S("dosage") && <th>Dosage</th>}{S("frequency") && <th>Frequency</th>}{S("duration") && <th>Duration</th>}{S("instructions") && <th>Instructions</th>}</tr></thead>
            <tbody>
              {meds.length === 0 ? <tr><td colSpan={7} className="py-1 text-slate-400">—</td></tr> :
                meds.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100 align-top">
                    <td className="py-0.5">{i + 1}</td>
                    <td className="font-medium">{m.name || "—"}</td>
                    {S("strength") && <td>{m.strength || "—"}</td>}
                    {S("dosage") && <td>{m.dosage || "—"}</td>}
                    {S("frequency") && <td>{m.frequency || "—"}</td>}
                    {S("duration") && <td>{m.duration || "—"}</td>}
                    {S("instructions") && <td>{m.instructions || "—"}</td>}
                  </tr>))}
            </tbody>
          </table>
          {S("advice") && <p className="mt-1"><b>Advice:</b> {rx?.advice || "—"}</p>}
          {S("follow_up") && <p className="mt-1"><b>Follow-up:</b> {rx?.follow_up_date ? dmy(rx.follow_up_date) : "—"}</p>}
        </div>
      )}
      {on("investigations") && mode === "digital" && invest.length > 0 && (
        <div style={{ ...box("investigations"), fontSize: "1em" }} data-testid="rx-zone-investigations">
          <p style={{ fontSize: "0.78em" }} className="font-bold uppercase tracking-wider text-slate-500">Investigations advised</p>
          <p className="mt-0.5">{invest.join(", ")}</p>
        </div>
      )}
      {on("signature") && mode !== "blank" && (
        <div style={{ ...box("signature"), fontSize: "0.95em" }} data-testid="rx-zone-signature">
          <div className="ml-auto w-56 border-t border-slate-800 pt-1 text-center">
            {template?.signatory_name
              ? `${template.signatory_name}${template.signatory_designation ? `, ${template.signatory_designation}` : ""}`
              : (rx?.doctor_name ? `Dr. ${rx.doctor_name}` : "Authorised signatory")}
          </div>
          {S("footer") && <p className="mt-2 text-center text-[9px]">{template?.footer_text || "—"}</p>}
        </div>
      )}
    </div>
  );
}

// A4/A5-sized paper surface for the Settings prescription designer. Mirrors the
// printed padding/width so the live preview matches the actual PDF output.
export function DesignerSurface({ paperSize = "A4", template = null, children }) {
  const w = paperSize === "A5" ? 559 : 794;
  return (
    <div className="overflow-auto rounded-lg border border-slate-300 bg-slate-200/60 p-4" style={{ maxHeight: 560 }}>
      <div className="mx-auto bg-white text-[12.5px] leading-relaxed text-slate-900 shadow-lg"
        style={{ width: w, padding: "16mm 14mm", ...documentStyle(template) }}
        data-testid="designer-preview-surface">
        {children}
      </div>
    </div>
  );
}

export const DocHeader = ({ business, docTitle, right }) => (
  <div className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4">
    <div className="flex items-start gap-4">
      {business?.logo_data && <img src={business.logo_data} alt="" className="h-14 w-14 object-contain" />}
      <div>
        <p className="font-ui text-[18px] font-extrabold uppercase tracking-tight">{business?.name || "—"}</p>
        {business?.address && <p className="text-[11px] text-slate-600">{business.address}</p>}
        <p className="text-[11px] text-slate-600">
          {[business?.city, business?.state, business?.pincode].filter(Boolean).join(", ")}
        </p>
        <p className="text-[11px] text-slate-600">
          {[business?.phone && `Ph: ${business.phone}`, business?.email].filter(Boolean).join(" · ")}
        </p>
        <p className="text-[11px] text-slate-600">
          {[business?.registration_no && `Reg. No: ${business.registration_no}`,
            business?.gstin && `GSTIN: ${business.gstin}`].filter(Boolean).join(" · ")}
        </p>
      </div>
    </div>
    <div className="text-right">
      <p className="font-ui text-[15px] font-bold uppercase tracking-wide text-[#EA580C]">{docTitle}</p>
      {right}
    </div>
  </div>
);

export const Grid = ({ rows, cols = 3 }) => (
  <div className={`mt-4 grid gap-x-6 gap-y-2 ${cols === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
    {rows.map(([k, v]) => (
      <div key={k}>
        <p className="text-[9.5px] uppercase tracking-wider text-slate-500">{k}</p>
        <p className="font-medium">{v === "" || v === null || v === undefined ? "—" : v}</p>
      </div>
    ))}
  </div>
);

export const Block = ({ title, children }) => (
  <div className="mt-5">
    <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">{title}</p>
    <div className="mt-2 whitespace-pre-wrap">{children || "—"}</div>
  </div>
);

export const SignatureRow = ({ label = "Doctor's signature", extra }) => (
  <div className="mt-12 flex items-end justify-between">
    <div className="text-[10px] text-slate-500">{extra}</div>
    <div className="w-56 border-t border-slate-800 pt-1 text-center text-[10.5px]">{label}</div>
  </div>
);

const patientLine = (p) => [
  p?.gender && titleize(p.gender),
  p?.age !== undefined && p?.age !== null && p?.age !== "" ? `${p.age} yrs` : null,
  p?.blood_group,
].filter(Boolean).join(" · ");

export function useActiveTemplate(module, docType, { suppressAuthRedirect = false } = {}) {
  const [tpl, setTpl] = useState(null);
  useEffect(() => {
    let live = true;
    if (!docType) { setTpl(null); return; }
    api.get("/templates/active", { params: { module, doc_type: docType }, suppressAuthRedirect })
      .then(({ data }) => { if (live) setTpl(data.template || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [module, docType, suppressAuthRedirect]);
  return tpl;
}

const tShow = (tpl, key, dflt = true) => {
  if (!tpl) return dflt;
  const f = tpl.fields || {};
  const hasSelection = Object.values(f).some(Boolean);
  if (!hasSelection) return dflt;
  return Boolean(f[key]);
};

export function TemplatedHeader({ template, business, docTitle, right }) {
  if (template?.letterhead_image && tShow(template, "letterhead", false)) {
    return (
      <div className="border-b-2 border-slate-900 pb-4" data-testid="doc-letterhead">
        <img src={template.letterhead_image} alt="" className="max-h-28 w-full object-contain" />
        {template.header_text && <p className="mt-2 text-center text-[11px] text-slate-600">{template.header_text}</p>}
        <div className="mt-2 text-right">
          <p className="font-ui text-[15px] font-bold uppercase tracking-wide text-[#EA580C]">{docTitle}</p>
          {right}
        </div>
      </div>
    );
  }
  const logo = template?.logo_image || business?.logo_data;
  const showBiz = template?.module === "hospital"
    ? tShow(template, "hospital_details", true)
    : tShow(template, "business_details", true);
  return (
    <div className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4">
      <div className="flex items-start gap-4">
        {tShow(template, "logo", true) && logo && <img src={logo} alt="" className="h-14 w-14 object-contain" />}
        <div>
          {tShow(template, "business_name", true) && (
            <p className="font-ui text-[18px] font-extrabold uppercase tracking-tight">
              {business?.name || "—"}
            </p>
          )}
          {showBiz && (
            <>
              {business?.address && <p className="text-[11px] text-slate-600">{business.address}</p>}
              <p className="text-[11px] text-slate-600">{[business?.city, business?.state, business?.pincode].filter(Boolean).join(", ")}</p>
              <p className="text-[11px] text-slate-600">{[business?.phone && `Ph: ${business.phone}`, business?.email].filter(Boolean).join(" · ")}</p>
              <p className="text-[11px] text-slate-600">{[business?.registration_no && `Reg. No: ${business.registration_no}`, business?.gstin && `GSTIN: ${business.gstin}`].filter(Boolean).join(" · ")}</p>
            </>
          )}
          {template?.header_text && <p className="text-[11px] text-slate-600 mt-1">{template.header_text}</p>}
        </div>
      </div>
      <div className="text-right">
        <p className="font-ui text-[15px] font-bold uppercase tracking-wide text-[#EA580C]">{docTitle}</p>
        {right}
      </div>
    </div>
  );
}

export function PrescriptionDoc({ rx, patient, business, template: tplProp }) {
  const docType = rx?.encounter_type === "opd" ? "opd_prescription" : "consultation_prescription";
  const fetched = useActiveTemplate("hospital", tplProp ? null : docType);
  const template = tplProp || fetched;
  if (!rx) return null;
  if ((template?.use_background && template?.letterhead_image) || template?.field_layout)
    return <RxPad template={template} rx={rx} patient={patient} business={business} />;
  const S = (k, d = true) => tShow(template, k, d);
  const rows = [];
  if (S("patient_name")) rows.push(["Patient", rx.patient_name]);
  if (S("uhid")) rows.push(["Patient ID (UHID)", rx.patient_uhid]);
  if (S("age_sex") && !(S("age", false) || S("gender", false))) rows.push(["Age / gender", patientLine({ ...patient, age: rx.patient_age ?? patient?.age, gender: rx.patient_gender || patient?.gender })]);
  if (S("age", false)) rows.push(["Age", rx.patient_age ?? patient?.age]);
  if (S("gender", false)) rows.push(["Gender", titleize(rx.patient_gender || patient?.gender || "—")]);
  if (S("patient_phone")) rows.push(["Mobile", rx.patient_phone || patient?.phone]);
  if (S("doctor")) rows.push(["Doctor", rx.doctor_name ? `Dr. ${rx.doctor_name}` : undefined]);
  if (S("doctor_qualification")) rows.push(["Qualification", rx.doctor_qualification]);
  if (S("doctor_registration")) rows.push(["Doctor registration", rx.doctor_registration]);
  if (S("registration_no")) rows.push(["Registration no.", rx.visit_id || rx.admission_id]);
  if (S("department")) rows.push(["Department", rx.department_name || rx.department]);
  if (S("prescription_no")) rows.push(["Prescription no.", rx.prescription_no]);
  if (S("date")) rows.push(["Encounter", rx.encounter_type ? titleize(rx.encounter_type) : "OPD"]);

  const medCols = [["#"], ["Medicine", "name"]];
  if (S("strength")) medCols.push(["Strength", "strength"]);
  if (S("dosage")) medCols.push(["Dosage", "dosage"]);
  if (S("frequency")) medCols.push(["Frequency", "frequency"]);
  if (S("duration")) medCols.push(["Duration", "duration"]);
  if (S("instructions")) medCols.push(["Instructions", "instructions"]);

  return (
    <>
      <TemplatedHeader template={template} business={business} docTitle="Prescription"
        right={<>
          {S("prescription_no") && <p className="text-[11px]">No: <b>{rx.prescription_no || "—"}</b></p>}
          {S("date") && <p className="text-[11px]">{dmyTime(rx.created_at)}</p>}
        </>} />
      <Grid rows={rows} />
      {S("symptoms") && <Block title="Symptoms">{rx.symptoms}</Block>}
      {S("diagnosis") && <Block title="Diagnosis">{rx.diagnosis}</Block>}
      {S("clinical_notes") && <Block title="Clinical notes">{rx.clinical_notes}</Block>}
      {S("treatment") && <Block title="Treatment">{rx.treatment}</Block>}
      {S("medicines") && (
        <div className="mt-5">
          <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Rx — medicines</p>
          <table className="mt-2 w-full border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-300 text-left">
                {medCols.map((c) => <th key={c[0]} className="py-1.5">{c[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {(rx.medicines || []).map((m, i) => (
                <tr key={i} className="border-b border-slate-100 align-top">
                  {medCols.map((c) => (
                    <td key={c[0]} className={`py-1.5 ${c[1] === "name" ? "font-medium" : ""}`}>
                      {c[0] === "#" ? i + 1 : (m[c[1]] || "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {S("advice") && <Block title="Advice">{rx.advice}</Block>}
      {S("follow_up") && <Block title="Follow-up">{rx.follow_up_date ? dmy(rx.follow_up_date) : "—"}</Block>}
      {S("signature") && (
        <SignatureRow
          label={template?.signatory_name
            ? `${template.signatory_name}${template.signatory_designation ? `, ${template.signatory_designation}` : ""}`
            : (rx.doctor_name ? `Dr. ${rx.doctor_name}` : "Doctor's signature")}
          extra={S("footer") ? template?.footer_text || "—" : ""} />
      )}
      {S("footer") && !S("signature") && <Block title="Footer">{template?.footer_text}</Block>}
    </>
  );
}

export function CaseSheetDoc({ data, template = null, docTitle = "IPD Case Sheet" }) {
  const { admission: a, patient: p, business, maternity } = data || {};
  if (!a) return null;
  const S = (key, dflt = true) => tShow(template, key, dflt);
  const admissionNo = a.admission_no || (a.admission_id ? a.admission_id.slice(-6).toUpperCase() : "—");
  return (
    <>
      <TemplatedHeader template={template} business={business} docTitle={docTitle}
        right={<>
          <p className="text-[11px]">IP No: <b>{admissionNo}</b></p>
          <p className="text-[11px]">Printed {dmyTime(new Date().toISOString())}</p>
        </>} />
      <Grid rows={[
        ...(S("patient_name") ? [["Patient", a.patient_name]] : []),
        ...(S("uhid") ? [["UHID", a.patient_uhid]] : []),
        ...(S("age_sex") ? [["Age / gender", patientLine(p)]] : []),
        ...(S("patient_phone") ? [["Mobile", a.patient_phone]] : []),
        ...(S("doctor") ? [["Consultant", a.doctor_name ? `Dr. ${a.doctor_name}` : "—"]] : []),
        ...(S("clinical_notes") ? [["Ward / bed", `${a.ward_name || "—"} · ${a.bed_number}`]] : []),
        ...(S("date") ? [["Admitted", dmyTime(a.admitted_at)]] : []),
      ]} />
      {S("diagnosis") && <Block title="Provisional diagnosis">{a.diagnosis}</Block>}
      {S("treatment") && <Block title="Treatment plan">{a.treatment_plan}</Block>}
      {S("clinical_notes") && <Block title="Admission notes">{a.notes}</Block>}
      {S("clinical_notes") && <Block title="Vitals on admission">{a.vitals}</Block>}
      {S("clinical_notes") && <div className="mt-5">
        <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Clinical progress & nursing notes</p>
        {(a.case_notes || []).length === 0 ? <p className="mt-2">No notes recorded.</p> : (
          <table className="mt-2 w-full border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-slate-300 text-left">
                <th className="py-1.5">Date & time</th><th className="py-1.5">Type</th>
                <th className="py-1.5">Vitals</th><th className="py-1.5">Note</th><th className="py-1.5">By</th>
              </tr>
            </thead>
            <tbody>
              {(a.case_notes || []).map((n) => (
                <tr key={n.note_id} className="border-b border-slate-100 align-top">
                  <td className="py-1.5 whitespace-nowrap">{dmyTime(n.at)}</td>
                  <td className="py-1.5">{titleize(n.note_type)}</td>
                  <td className="py-1.5">{n.vitals || "—"}</td>
                  <td className="py-1.5">{n.note}</td>
                  <td className="py-1.5">{staffName(n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>}
      {S("treatment") && (a.services || []).filter((s) => s.status !== "reversed").length > 0 && (
        <div className="mt-5">
          <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Treatments & charges</p>
          <table className="mt-2 w-full border-collapse text-[11.5px]">
            <tbody>
              {a.services.filter((s) => s.status !== "reversed").map((s, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5">{dmy(s.at)}</td>
                  <td className="py-1.5">{s.description}</td>
                  <td className="py-1.5 text-right">{inr(s.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {S("clinical_notes") && maternity && (
        <Block title="Maternity linkage">
          {`Gravida ${maternity.gravida || 0} / Para ${maternity.para || 0} · EDD ${maternity.edd ? dmy(maternity.edd) : "—"} · Status ${titleize(maternity.status)}`}
        </Block>
      )}
      {S("signature") && <SignatureRow label={a.doctor_name ? `Dr. ${a.doctor_name}` : "Doctor's signature"}
        extra={template?.footer_text || "Confidential medical record."} />}
    </>
  );
}

export function DischargeSummaryDoc({ data, template = null }) {
  const { admission: a, patient: p, business } = data || {};
  if (!a) return null;
  const S = (key, dflt = true) => tShow(template, key, dflt);
  const admissionNo = a.admission_no || (a.admission_id ? a.admission_id.slice(-6).toUpperCase() : "—");
  return (
    <>
      <TemplatedHeader template={template} business={business} docTitle="Discharge Summary"
        right={<>
          <p className="text-[11px]">IP No: <b>{admissionNo}</b></p>
          <p className="text-[11px]">{dmyTime(a.discharged_at || new Date().toISOString())}</p>
        </>} />
      <Grid rows={[
        ...(S("patient_name") ? [["Patient", a.patient_name]] : []),
        ...(S("uhid") ? [["UHID", a.patient_uhid]] : []),
        ...(S("age_sex") ? [["Age / gender", patientLine(p)]] : []),
        ...(S("doctor") ? [["Consultant", a.doctor_name ? `Dr. ${a.doctor_name}` : "—"]] : []),
        ...(S("patient_phone") ? [["Mobile", a.patient_phone]] : []),
        ...(S("date") ? [["Date of admission", dmyTime(a.admitted_at)]] : []),
        ...(S("date") ? [["Date of discharge", a.discharged_at ? dmyTime(a.discharged_at) : "—"]] : []),
      ]} />
      {S("diagnosis") && <Block title="Diagnosis at admission">{a.diagnosis}</Block>}
      {S("diagnosis") && <Block title="Final / discharge diagnosis">{a.discharge_diagnosis}</Block>}
      {S("clinical_notes") && <Block title="Hospital course">{a.hospital_course}</Block>}
      {S("treatment") && <Block title="Treatment & procedures performed">{a.procedures || a.treatment_plan}</Block>}
      {S("clinical_notes") && <Block title="Condition at discharge">{a.condition_at_discharge}</Block>}
      {S("medicines") && <Block title="Discharge medicines">{a.discharge_medicines}</Block>}
      {S("clinical_notes") && <Block title="Summary">{a.summary}</Block>}
      {S("follow_up") && <Block title="Follow-up instructions">
        {[a.follow_up_date ? `Review on ${dmy(a.follow_up_date)}.` : "", a.follow_up_instructions]
          .filter(Boolean).join(" ")}
      </Block>}
      {S("signature") && <SignatureRow label={a.doctor_name ? `Dr. ${a.doctor_name}` : "Doctor's signature"}
        extra={template?.footer_text || "Report to the hospital immediately if symptoms worsen."} />}
    </>
  );
}

export function HospitalInvoiceDoc({ invoice: i, patient, business }) {
  if (!i) return null;
  const isIpd = i.invoice_type === "ipd" || i.invoice_type === "maternity";
  const admissionRef = isIpd ? (i.admission_no || i.reference_id) : null;
  return (
    <>
      <DocHeader business={business} docTitle="Tax Invoice"
        right={<>
          <p className="text-[11px]">Invoice: <b>{i.invoice_no}</b></p>
          {admissionRef && <p className="text-[11px]">Admission: <b>{admissionRef}</b></p>}
          <p className="text-[11px]">{dmyTime(i.created_at)}</p>
          <p className="text-[11px] uppercase">Status: <b>{titleize(i.status)}</b></p>
        </>} />
      <Grid cols={3} rows={[
        ["Billed to", i.patient_name], ["UHID", patient?.uhid],
        ["Mobile", patient?.phone],
        ["Bill type", titleize(i.invoice_type)],
        ["Doctor", i.doctor_name ? `Dr. ${i.doctor_name}` : "—"],
        ["Payment method", (i.payments || []).length
          ? i.payments.map((payment) => titleize(payment.mode)).join(" + ")
          : titleize(i.payment_method || "cash")],
        ["Reference", i.reference_no || admissionRef || "—"],
        ["City", patient?.city],
        ["Bill date", dmy(i.created_at)],
      ]} />
      <table className="mt-6 w-full border-collapse text-[11.5px]">
        <thead>
          <tr className="border-y-2 border-slate-800 text-left">
            <th className="py-2 w-6">#</th>
            <th className="py-2">Particulars</th>
            <th className="py-2 text-right">Qty</th>
            <th className="py-2 text-right">Rate</th>
            <th className="py-2 text-right">Discount</th>
            <th className="py-2 text-right">Tax %</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(i.items || []).map((l, idx) => (
            <tr key={idx} className="border-b border-slate-200 align-top">
              <td className="py-1.5">{idx + 1}</td>
              <td className="py-1.5">
                <span className="font-medium">{l.description}</span>
                {l.detail && <span className="block text-[10px] text-slate-500">{l.detail}</span>}
              </td>
              <td className="py-1.5 text-right">{l.quantity ?? 1}</td>
              <td className="py-1.5 text-right">{inr(l.rate ?? l.amount)}</td>
              <td className="py-1.5 text-right">{inr(l.discount || 0)}</td>
              <td className="py-1.5 text-right">{l.tax_percent || 0}%</td>
              <td className="py-1.5 text-right font-medium">{inr(l.line_total ?? l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex justify-end">
        <div className="w-72 space-y-1 text-[12px]">
          <Row label="Taxable value" value={inr(i.subtotal)} />
          <Row label="Tax" value={inr(i.tax)} />
          {i.discount > 0 && <Row
            label={`Bill discount${i.discount_type === "percent" ? ` (${i.discount_value}%)` : ""}`}
            value={`- ${inr(i.discount)}`} />}
          <div className="flex justify-between border-y-2 border-slate-800 py-1.5 font-ui text-[14px] font-bold">
            <span>Grand total</span><span>{inr(i.total)}</span>
          </div>
          <Row label="Paid" value={inr(i.paid)} />
          {i.advance_applied > 0 && <Row label="Advance / deposit applied" value={inr(i.advance_applied)} />}
          {i.advance_refunded > 0 && <Row label="Advance refunded" value={inr(i.advance_refunded)} />}
          {i.advance_refundable > 0 && <Row label="Refundable advance" value={inr(i.advance_refundable)} />}
          <Row label="Outstanding" value={inr(i.outstanding)} />
        </div>
      </div>
      {(i.payments || []).length > 0 && (
        <div className="mt-5">
          <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Receipts</p>
          {i.payments.map((p) => (
            <p key={p.payment_id} className="mt-1 text-[11px]">
              {dmyTime(p.at)} · {inr(p.amount)} · {titleize(p.mode)}
              {p.reference_no ? ` · Ref ${p.reference_no}` : ""} · {staffName(p)}
            </p>
          ))}
        </div>
      )}
      {i.notes && <Block title="Notes">{i.notes}</Block>}
      <SignatureRow label="Authorised signatory"
        extra={business?.general?.invoice_terms || "Please retain this invoice for insurance and reimbursement."} />
    </>
  );
}

export function MaternityDoc({ data, kind = "case" }) {
  const { maternity: m, patient: p, business } = data || {};
  if (!m) return null;
  const d = m.delivery;
  const titles = { case: "Maternity Case Sheet", delivery: "Delivery Record", discharge: "Maternity Discharge Summary" };
  return (
    <>
      <DocHeader business={business} docTitle={titles[kind]}
        right={<>
          <p className="text-[11px]">Case: <b>{m.maternity_id?.slice(-8).toUpperCase()}</b></p>
          <p className="text-[11px]">Printed {dmyTime(new Date().toISOString())}</p>
        </>} />
      <Grid rows={[
        ["Mother", m.patient_name], ["UHID", m.patient_uhid], ["Age / gender", patientLine(p)],
        ["Mobile", m.patient_phone], ["Blood group", m.blood_group || p?.blood_group],
        ["Allergies", m.allergies || p?.allergies],
        ["LMP", m.lmp_date ? dmy(m.lmp_date) : "—"], ["EDD", m.edd ? dmy(m.edd) : "—"],
        ["Gravida / Para", `G${m.gravida || 0} P${m.para || 0} L${m.living_children || 0} A${m.abortions || 0}`],
        ["Risk", m.high_risk ? "High risk" : "Normal"],
        ["Consultant", m.doctor_name || "—"], ["Status", titleize(m.status)],
      ]} />
      {kind !== "delivery" && (
        <>
          <Block title="Obstetric history">{m.obstetric_history}</Block>
          <Block title="Medical history">{m.medical_history}</Block>
          <Block title="Antenatal notes">{m.antenatal_notes}</Block>
          <div className="mt-5">
            <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Antenatal visits</p>
            {(m.visits || []).length === 0 ? <p className="mt-2">No visits recorded.</p> : (
              <table className="mt-2 w-full border-collapse text-[11.5px]">
                <thead>
                  <tr className="border-b border-slate-300 text-left">
                    <th className="py-1.5">Date</th><th className="py-1.5">Weight</th><th className="py-1.5">BP</th>
                    <th className="py-1.5">Hb</th><th className="py-1.5">FH</th><th className="py-1.5">FHR</th>
                    <th className="py-1.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {m.visits.map((v, i) => (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="py-1.5">{dmy(v.at)}</td><td className="py-1.5">{v.weight || "—"}</td>
                      <td className="py-1.5">{v.bp || "—"}</td><td className="py-1.5">{v.haemoglobin || "—"}</td>
                      <td className="py-1.5">{v.fundal_height || "—"}</td>
                      <td className="py-1.5">{v.fetal_heart_rate || "—"}</td>
                      <td className="py-1.5">{[v.symptoms, v.examination, v.notes].filter(Boolean).join(" · ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {(m.investigations || []).length > 0 && (
            <div className="mt-5">
              <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Investigations</p>
              {m.investigations.map((x, i) => (
                <p key={i} className="mt-1 text-[11.5px]">
                  {dmy(x.taken_on || x.at)} · {titleize(x.kind)} · <b>{x.name}</b> — {x.result || "—"} {x.notes ? `(${x.notes})` : ""}
                </p>
              ))}
            </div>
          )}
          {m.labour && (
            <Block title="Labour observations">
              {[m.labour.labour_status, m.labour.cervical_findings, m.labour.fetal_monitoring, m.labour.notes]
                .filter(Boolean).join(" | ")}
            </Block>
          )}
        </>
      )}
      {d && (
        <>
          <div className="mt-5">
            <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Delivery record</p>
            <Grid rows={[
              ["Date & time", dmyTime(d.delivery_at)], ["Mode", titleize(d.delivery_type)],
              ["Outcome", titleize(d.outcome)], ["Indication", d.indication],
              ["Complications", d.complications], ["Attending", d.attending_doctor],
            ]} />
            <Block title="Delivery notes">{d.notes}</Block>
          </div>
          <div className="mt-5">
            <p className="border-b border-slate-300 pb-1 font-ui text-[11px] font-bold uppercase tracking-wider text-slate-700">Newborn record</p>
            <table className="mt-2 w-full border-collapse text-[11.5px]">
              <thead>
                <tr className="border-b border-slate-300 text-left">
                  <th className="py-1.5">Baby</th><th className="py-1.5">UHID</th><th className="py-1.5">Gender</th>
                  <th className="py-1.5">Weight (kg)</th><th className="py-1.5">Birth time</th>
                  <th className="py-1.5">Apgar 1'/5'</th><th className="py-1.5">Condition</th>
                </tr>
              </thead>
              <tbody>
                {(d.babies || []).map((b) => (
                  <tr key={b.baby_id} className="border-b border-slate-100">
                    <td className="py-1.5">{b.name || "—"}</td><td className="py-1.5">{b.uhid || "—"}</td>
                    <td className="py-1.5">{titleize(b.gender)}</td><td className="py-1.5">{b.weight_kg || "—"}</td>
                    <td className="py-1.5">{b.birth_time || "—"}</td>
                    <td className="py-1.5">{[b.apgar_1, b.apgar_5].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="py-1.5">{b.condition || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {kind === "discharge" && (
        <>
          {m.postpartum && (
            <Block title="Postpartum course">
              {[m.postpartum.observations, m.postpartum.bleeding && `Bleeding: ${m.postpartum.bleeding}`,
                m.postpartum.pain && `Pain: ${m.postpartum.pain}`,
                m.postpartum.breastfeeding && `Breastfeeding: ${m.postpartum.breastfeeding}`,
                m.postpartum.medications && `Medications: ${m.postpartum.medications}`]
                .filter(Boolean).join(" | ")}
            </Block>
          )}
          <Block title="Mother — discharge summary">{m.discharge?.mother_summary}</Block>
          <Block title="Mother — medications">{m.discharge?.mother_medications}</Block>
          <Block title="Mother — follow-up">{m.discharge?.mother_follow_up}</Block>
          <Block title="Baby — condition at discharge">{m.discharge?.baby_condition}</Block>
          <Block title="Baby — feeding instructions">{m.discharge?.baby_feeding}</Block>
          <Block title="Baby — vaccination">{m.discharge?.baby_vaccination}</Block>
          <Block title="Baby — follow-up">{m.discharge?.baby_follow_up}</Block>
        </>
      )}
      <SignatureRow label={m.doctor_name ? `Dr. ${m.doctor_name}` : "Consultant obstetrician"}
        extra="Confidential maternity record." />
    </>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-600">{label}</span><span>{value}</span></div>
);
